<template>
  <div class="ddc-board-canvas">
    <!-- Toolbar -->
    <div class="canvas-toolbar q-pa-sm bg-grey-2">
      <div class="row items-center q-gutter-sm">
        <q-btn icon="zoom_in" flat dense @click="zoomIn">
          <q-tooltip>Zoom In</q-tooltip>
        </q-btn>
        <q-btn icon="zoom_out" flat dense @click="zoomOut">
          <q-tooltip>Zoom Out</q-tooltip>
        </q-btn>
        <q-btn icon="fit_screen" flat dense @click="fitToScreen">
          <q-tooltip>Fit to Screen</q-tooltip>
        </q-btn>
        <q-separator vertical />
        <q-btn
          icon="grid_on"
          flat
          dense
          :color="showGrid ? 'primary' : 'grey'"
          @click="toggleGrid"
        >
          <q-tooltip>{{ showGrid ? 'Hide' : 'Show' }} Grid</q-tooltip>
        </q-btn>
        <q-btn
          icon="straighten"
          flat
          dense
          :color="snapToGrid ? 'primary' : 'grey'"
          @click="toggleSnap"
        >
          <q-tooltip>{{ snapToGrid ? 'Disable' : 'Enable' }} Snap to Grid</q-tooltip>
        </q-btn>
        <q-space />
        <q-chip v-if="itemCount > 0" color="primary" text-color="white" size="sm">
          {{ itemCount }} item{{ itemCount !== 1 ? 's' : '' }}
        </q-chip>
        <q-btn
          icon="save"
          label="Save Layout"
          color="primary"
          :loading="saving"
          :disable="!hasChanges"
          @click="handleSaveLayout"
        />
      </div>
    </div>

    <div class="row no-wrap" style="height: calc(100vh - 250px)">
      <!-- Component Library (Left Panel) -->
      <div class="component-library col-auto q-pa-sm bg-grey-1" style="width: 280px; overflow-y: auto;">
        <div class="text-subtitle2 q-mb-sm text-weight-bold">Controllers</div>
        <q-list dense separator class="rounded-borders">
          <q-item
            v-for="controller in availableControllers"
            :key="controller.id"
            clickable
            draggable="true"
            class="bg-white cursor-grab"
            @dragstart="startDragController(controller, $event)"
          >
            <q-item-section avatar>
              <q-icon name="memory" color="green" />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-weight-medium">{{ controller.name }}</q-item-label>
              <q-item-label v-if="controller.equipment_served?.length" caption>
                <q-icon name="hub" size="xs" class="q-mr-xs" />
                {{ controller.equipment_served.map(e => e.name).join(', ') }}
              </q-item-label>
              <q-item-label v-if="controller.io_summary?.total > 0" caption>
                <q-icon name="cable" size="xs" class="q-mr-xs" />
                I/O: {{ controller.io_summary.total }} points
              </q-item-label>
              <q-item-label caption class="text-grey-5">
                {{ controller.dimensions?.width }}×{{ controller.dimensions?.height }}mm
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item v-if="availableControllers.length === 0 && placedControllerCount > 0">
            <q-item-section>
              <q-item-label class="text-grey-6 text-italic">
                All controllers are placed on the board.
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item v-else-if="availableControllers.length === 0">
            <q-item-section>
              <q-item-label class="text-grey-6 text-italic">
                No controllers found. Add controllers as children of this DDC Board in the site hierarchy.
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <q-separator class="q-my-md" />

        <div class="text-subtitle2 q-mb-sm text-weight-bold">Accessories</div>
        <q-list dense separator class="rounded-borders">
          <q-expansion-item
            v-for="accessory in accessories"
            :key="accessory.type"
            :label="accessory.name"
            dense
            header-class="text-caption bg-white"
            expand-separator
          >
            <q-list dense>
              <q-item
                v-for="variant in accessory.variants"
                :key="variant.name"
                clickable
                draggable="true"
                class="bg-grey-1 cursor-grab"
                @dragstart="startDragAccessory(accessory, variant, $event)"
              >
                <q-item-section>
                  <q-item-label class="text-caption">{{ variant.name }}</q-item-label>
                  <q-item-label caption>
                    {{ variant.dimensions.width }}×{{ variant.dimensions.height }}mm
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>
        </q-list>
      </div>

      <!-- Canvas Area (Center) -->
      <div
        ref="canvasContainer"
        class="canvas-container col q-pa-md"
        @dragover.prevent
        @drop="handleDrop"
      >
        <!-- Board Dimensions Input -->
        <div class="board-dimensions q-mb-md row q-gutter-sm items-center">
          <q-input
            v-model.number="boardWidth"
            label="Width (mm)"
            type="number"
            outlined
            dense
            style="width: 120px"
            :min="100"
            :max="2000"
            @update:model-value="handleDimensionChange"
          />
          <span class="text-grey-7">×</span>
          <q-input
            v-model.number="boardHeight"
            label="Height (mm)"
            type="number"
            outlined
            dense
            style="width: 120px"
            :min="100"
            :max="2000"
            @update:model-value="handleDimensionChange"
          />
          <q-input
            v-model="boardName"
            label="Board Name"
            outlined
            dense
            style="width: 250px"
            @update:model-value="handleDimensionChange"
          />
        </div>

        <!-- Canvas SVG -->
        <div
          class="canvas-wrapper"
          :style="canvasWrapperStyle"
        >
          <svg
            ref="canvasSvg"
            :width="canvasWidth"
            :height="canvasHeight"
            :viewBox="`0 0 ${boardWidth} ${boardHeight}`"
            class="board-canvas"
            @click.self="deselectAll"
          >
            <!-- Grid -->
            <defs>
              <pattern id="grid" :width="gridSize" :height="gridSize" patternUnits="userSpaceOnUse">
                <path
                  :d="`M ${gridSize} 0 L 0 0 0 ${gridSize}`"
                  fill="none"
                  stroke="#e0e0e0"
                  stroke-width="0.5"
                />
              </pattern>
            </defs>
            <rect v-if="showGrid" width="100%" height="100%" fill="url(#grid)" />

            <!-- Board Border -->
            <rect
              x="0"
              y="0"
              :width="boardWidth"
              :height="boardHeight"
              fill="#fafafa"
              stroke="#333"
              stroke-width="2"
            />

            <!-- Placed Items (sorted by z_index - DIN rails/cable ducts render first/behind) -->
            <g
              v-for="item in sortedLayoutItems"
              :key="item.id"
              :transform="getItemTransform(item)"
              class="board-item"
              :class="{ selected: selectedItem?.id === item.id }"
              @click.stop="selectItem(item)"
              @mousedown="startDrag(item, $event)"
              @contextmenu.prevent="showItemContextMenu(item, $event)"
            >
              <!-- Inner group applies rotation transform -->
              <g :transform="getItemRotationTransform(item)">
                <!-- Controller Item -->
                <g v-if="item.type === 'controller'">
                  <rect
                    :width="item.size.width"
                    :height="item.size.height"
                    fill="#e8f5e9"
                    stroke="#4caf50"
                    stroke-width="2"
                    rx="4"
                  />
                  <text x="5" y="15" font-size="10" fill="#333" font-weight="bold">
                    {{ item.name }}
                  </text>
                  <text x="5" y="28" font-size="8" fill="#666">
                    {{ item.equipment_served }}
                  </text>
                  <text v-if="item.io_summary" x="5" :y="item.size.height - 8" font-size="8" fill="#666">
                    I/O: {{ item.io_summary.used || 0 }}/{{ item.io_summary.total || 0 }}
                  </text>
                </g>

                <!-- Accessory Item -->
                <g v-else-if="item.type === 'accessory'">
                  <!-- DIN Rail - metallic look with lines -->
                  <g v-if="item.accessory_type === 'din_rail'">
                    <rect
                      :width="item.size.width"
                      :height="item.size.height"
                      :fill="getAccessoryColor(item.accessory_type)"
                      stroke="#757575"
                      stroke-width="1"
                    />
                    <!-- Rail mounting slots -->
                    <line
                      v-for="i in Math.floor(item.size.width / 30)"
                      :key="'slot-' + i"
                      :x1="i * 30"
                      y1="3"
                      :x2="i * 30"
                      :y2="item.size.height - 3"
                      stroke="#9e9e9e"
                      stroke-width="1"
                    />
                  </g>
                  <!-- Cable Duct - slotted pattern -->
                  <g v-else-if="item.accessory_type === 'cable_duct'">
                    <rect
                      :width="item.size.width"
                      :height="item.size.height"
                      :fill="getAccessoryColor(item.accessory_type)"
                      stroke="#607d8b"
                      stroke-width="1"
                      rx="1"
                    />
                    <!-- Slotted pattern on top -->
                    <rect
                      v-for="i in Math.floor(item.size.width / 15)"
                      :key="'duct-slot-' + i"
                      :x="i * 15 + 3"
                      y="2"
                      width="8"
                      :height="item.size.height - 4"
                      fill="#78909c"
                      rx="1"
                    />
                  </g>
                  <!-- Standard accessory -->
                  <g v-else>
                    <rect
                      :width="item.size.width"
                      :height="item.size.height"
                      :fill="getAccessoryColor(item.accessory_type)"
                      stroke="#666"
                      stroke-width="1"
                      rx="2"
                    />
                    <text
                      :x="item.size.width / 2"
                      :y="item.size.height / 2 + 3"
                      font-size="8"
                      fill="#333"
                      text-anchor="middle"
                      font-weight="bold"
                    >
                      {{ item.name }}
                    </text>
                  </g>
                </g>
              </g>

              <!-- Selection handles use DISPLAY size (rotated bounding box) -->
              <g v-if="selectedItem?.id === item.id" class="selection-handles">
                <rect
                  x="-2"
                  y="-2"
                  :width="getItemDisplaySize(item).width + 4"
                  :height="getItemDisplaySize(item).height + 4"
                  fill="none"
                  stroke="#2196f3"
                  stroke-width="2"
                  stroke-dasharray="4,2"
                />
                <!-- Rotation handle -->
                <circle
                  :cx="getItemDisplaySize(item).width / 2"
                  cy="-15"
                  r="6"
                  fill="#2196f3"
                  stroke="white"
                  stroke-width="2"
                  class="rotation-handle"
                  @mousedown.stop="startRotate(item, $event)"
                />
                <line
                  :x1="getItemDisplaySize(item).width / 2"
                  y1="-9"
                  :x2="getItemDisplaySize(item).width / 2"
                  y2="-2"
                  stroke="#2196f3"
                  stroke-width="2"
                />
              </g>
            </g>
          </svg>
        </div>

        <!-- Spare Space Indicator -->
        <div class="spare-space-indicator q-mt-md">
          <q-linear-progress
            :value="1 - (spareSpacePercentage / 100)"
            :color="spareSpaceColor"
            size="24px"
          >
            <div class="absolute-full flex flex-center">
              <span class="text-white text-caption">
                {{ spareSpacePercentage }}% spare ({{ Math.round(spareSpaceMm2 / 1000) }}cm²)
              </span>
            </div>
          </q-linear-progress>
        </div>
      </div>

      <!-- Properties Panel (Right) -->
      <div class="properties-sidebar col-auto bg-white" style="width: 280px; overflow-y: auto;">
        <BoardPropertiesPanel
          :selected-item="selectedItem"
          @property-changed="handlePropertyChanged"
          @delete-item="handleDeleteItem"
        />
      </div>
    </div>

    <!-- Help text -->
    <div class="q-pa-sm text-caption text-grey-7">
      <q-icon name="info" size="xs" class="q-mr-xs" />
      Drag controllers from the left panel onto the board. Controllers must be created as equipment in the site hierarchy first.
      Right-click items to delete. Click canvas to deselect.
    </div>

    <!-- Context Menu for Items -->
    <q-menu
      v-model="showContextMenu"
      :target="contextMenuTarget"
      context-menu
      anchor="top left"
      self="top left"
    >
      <q-list dense style="min-width: 150px">
        <q-item v-close-popup clickable @click="deleteContextMenuItem">
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete</q-item-section>
        </q-item>
      </q-list>
    </q-menu>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useQuasar } from 'quasar';
  import { useBoardLayout } from '@/composables/brick-schema/documentation/useBoardLayout';
  import BoardPropertiesPanel from './ddc/BoardPropertiesPanel.vue';

  const props = defineProps({
    equipmentId: { type: Number, required: true },
    siteId: { type: Number, required: true },
  });

  const $q = useQuasar();
  const {
    layout,
    loading,
    saving,
    accessories,
    availableControllers,
    loadLayout,
    saveLayout,
    addItem,
    removeItem,
    loadAccessories,
    loadAvailableControllers,
  } = useBoardLayout(props.siteId, props.equipmentId);

  // Canvas state
  const boardWidth = ref(800);
  const boardHeight = ref(600);
  const boardName = ref('DDC Board');
  const zoom = ref(1);
  const showGrid = ref(true);
  const snapToGrid = ref(true);
  const gridSize = ref(20);
  const selectedItem = ref(null);
  const layoutItems = ref([]);
  const hasChanges = ref(false);

  // Canvas refs
  const canvasContainer = ref(null);
  const canvasSvg = ref(null);

  // Drag state
  const dragging = ref(false);
  const dragOffset = ref({ x: 0, y: 0 });

  // Context menu state
  const showContextMenu = ref(false);
  const contextMenuTarget = ref(null);
  const contextMenuItem = ref(null);

  // Computed
  const canvasWidth = computed(() => boardWidth.value * zoom.value);
  const canvasHeight = computed(() => boardHeight.value * zoom.value);
  const canvasWrapperStyle = computed(() => ({
    width: '100%',
    height: 'calc(100% - 120px)',
    overflow: 'auto',
    backgroundColor: '#f0f0f0',
    borderRadius: '4px',
    padding: '20px',
  }));

  const itemCount = computed(() => layoutItems.value.length);

  // Count controllers placed on the board
  const placedControllerCount = computed(() =>
    layoutItems.value.filter((item) => item.type === 'controller').length,
  );

  // Sort items by z_index so DIN rails and cable ducts render behind mounted components
  const sortedLayoutItems = computed(() => {
    return [...layoutItems.value].sort((a, b) => {
      const aZ = a.z_index ?? 10; // Default z_index is 10 for mounted components
      const bZ = b.z_index ?? 10;
      return aZ - bZ;
    });
  });

  const spareSpaceMm2 = computed(() => {
    const totalArea = boardWidth.value * boardHeight.value;
    const usedArea = layoutItems.value.reduce((sum, item) => {
      return sum + (item.size.width * item.size.height);
    }, 0);
    return totalArea - usedArea;
  });

  const spareSpacePercentage = computed(() => {
    const totalArea = boardWidth.value * boardHeight.value;
    if (totalArea === 0) {return 0;}
    return Math.round((spareSpaceMm2.value / totalArea) * 100);
  });

  const spareSpaceColor = computed(() => {
    const pct = spareSpacePercentage.value;
    if (pct < 10) {return 'negative';}
    if (pct < 25) {return 'warning';}
    return 'positive';
  });

  // Methods
  const markAsChanged = () => {
    hasChanges.value = true;
  };

  const handleDimensionChange = () => {
    markAsChanged();
  };

  const zoomIn = () => {
    zoom.value = Math.min(zoom.value + 0.1, 2);
  };

  const zoomOut = () => {
    zoom.value = Math.max(zoom.value - 0.1, 0.5);
  };

  const fitToScreen = () => {
    zoom.value = 1;
  };

  const toggleGrid = () => {
    showGrid.value = !showGrid.value;
  };

  const toggleSnap = () => {
    snapToGrid.value = !snapToGrid.value;
  };

  const startDragController = (controller, event) => {
    // Format equipment served as display string
    const equipmentNames = controller.equipment_served?.map((e) => e.name).join(', ') || '';

    const data = {
      type: 'controller',
      controller_id: controller.id,
      name: controller.name,
      equipment_served: equipmentNames,
      equipment_served_list: controller.equipment_served || [],
      size: controller.dimensions || { width: 120, height: 80 },
      io_summary: controller.io_summary || { total: 0, used: 0, available: 0 },
    };
    event.dataTransfer.setData('application/json', JSON.stringify(data));
    event.dataTransfer.effectAllowed = 'copy';
  };

  const startDragAccessory = (accessory, variant, event) => {
    const data = {
      type: 'accessory',
      accessory_type: accessory.type,
      name: variant.name,
      size: variant.dimensions,
      z_index: accessory.z_index ?? 10, // Include z_index for proper layering
    };
    event.dataTransfer.setData('application/json', JSON.stringify(data));
    event.dataTransfer.effectAllowed = 'copy';
  };

  const handleDrop = async (event) => {
    event.preventDefault();

    try {
      const data = JSON.parse(event.dataTransfer.getData('application/json'));
      const rect = canvasSvg.value.getBoundingClientRect();

      // Calculate position relative to SVG with zoom
      let x = (event.clientX - rect.left - 20) / zoom.value; // -20 for padding
      let y = (event.clientY - rect.top - 20) / zoom.value;

      // Snap to grid if enabled
      if (snapToGrid.value) {
        x = Math.round(x / gridSize.value) * gridSize.value;
        y = Math.round(y / gridSize.value) * gridSize.value;
      }

      // Ensure item is within board bounds
      x = Math.max(0, Math.min(x, boardWidth.value - data.size.width));
      y = Math.max(0, Math.min(y, boardHeight.value - data.size.height));

      const newItem = {
        ...data,
        id: crypto.randomUUID(),
        position: { x, y },
      };

      layoutItems.value.push(newItem);
      markAsChanged();

      // If it's a controller, remove it from the available list immediately
      if (data.type === 'controller' && data.controller_id) {
        availableControllers.value = availableControllers.value.filter(
          (c) => c.id !== data.controller_id,
        );
      }

      $q.notify({
        type: 'positive',
        message: 'Item added to board',
        timeout: 1000,
      });
    } catch (error) {
      console.error('[Board Canvas] Drop error:', error);
      $q.notify({
        type: 'negative',
        message: 'Failed to add item',
      });
    }
  };

  const startDrag = (item, event) => {
    if (event.button !== 0) {return;} // Only left mouse button

    selectedItem.value = item;
    dragging.value = true;

    const rect = canvasSvg.value.getBoundingClientRect();
    dragOffset.value = {
      x: (event.clientX - rect.left - 20) / zoom.value - item.position.x,
      y: (event.clientY - rect.top - 20) / zoom.value - item.position.y,
    };

    const handleMouseMove = (e) => {
      if (!dragging.value) {return;}

      const rect = canvasSvg.value.getBoundingClientRect();
      let x = (e.clientX - rect.left - 20) / zoom.value - dragOffset.value.x;
      let y = (e.clientY - rect.top - 20) / zoom.value - dragOffset.value.y;

      // Snap to grid if enabled
      if (snapToGrid.value) {
        x = Math.round(x / gridSize.value) * gridSize.value;
        y = Math.round(y / gridSize.value) * gridSize.value;
      }

      // Keep within bounds - use DISPLAY size (rotated dimensions) for collision
      const displaySize = getItemDisplaySize(item);
      x = Math.max(0, Math.min(x, boardWidth.value - displaySize.width));
      y = Math.max(0, Math.min(y, boardHeight.value - displaySize.height));

      item.position.x = x;
      item.position.y = y;
      markAsChanged();
    };

    const handleMouseUp = () => {
      dragging.value = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const selectItem = (item) => {
    selectedItem.value = item;
  };

  const deselectAll = () => {
    selectedItem.value = null;
  };

  const removeSelectedItem = () => {
    if (!selectedItem.value) {return;}

    $q.dialog({
      title: 'Remove Item',
      message: `Remove ${selectedItem.value.name} from the board?`,
      cancel: true,
      persistent: true,
    }).onOk(() => {
      layoutItems.value = layoutItems.value.filter((item) => item.id !== selectedItem.value.id);
      selectedItem.value = null;
      markAsChanged();

      $q.notify({
        type: 'positive',
        message: 'Item removed from board',
      });
    });
  };

  // Context menu functions
  const showItemContextMenu = (item, event) => {
    contextMenuItem.value = item;
    contextMenuTarget.value = event.target;
    showContextMenu.value = true;
  };

  const deleteContextMenuItem = () => {
    if (!contextMenuItem.value) {return;}

    const item = contextMenuItem.value;

    $q.dialog({
      title: 'Remove Item',
      message: `Remove ${item.name} from the board?`,
      cancel: true,
      persistent: true,
    }).onOk(() => {
      // If it's a controller, add it back to the available list
      if (item.type === 'controller' && item.controller_id) {
        availableControllers.value.push({
          id: item.controller_id,
          name: item.name,
          equipment_served: item.equipment_served_list || [],
          dimensions: item.size,
          io_summary: item.io_summary,
        });
      }

      layoutItems.value = layoutItems.value.filter((i) => i.id !== item.id);
      if (selectedItem.value?.id === item.id) {
        selectedItem.value = null;
      }
      contextMenuItem.value = null;
      markAsChanged();

      $q.notify({
        type: 'positive',
        message: 'Item removed from board',
      });
    });
  };

  const getAccessoryColor = (type) => {
    const colors = {
      power_supply: '#fff3e0',    // Light orange
      network_switch: '#e3f2fd',  // Light blue
      terminal_block: '#f3e5f5',  // Light purple
      din_rail: '#bdbdbd',        // Medium grey (metal rail)
      cable_duct: '#90a4ae',      // Blue-grey (slotted cable duct)
      circuit_breaker: '#ffebee', // Light red
      relay: '#e8f5e9',           // Light green
    };
    return colors[type] || '#f5f5f5';
  };

  // Rotation helpers
  const getItemTransform = (item) => {
    const rotation = item.rotation || 0;
    // Simply translate to position - no rotation compensation needed
    // The position represents the top-left of the item's bounding box
    return `translate(${item.position.x}, ${item.position.y})`;
  };

  // Get the inner transform for rotating the content within its bounding box
  const getItemRotationTransform = (item) => {
    const rotation = item.rotation || 0;
    if (rotation === 0) {return '';}

    const w = item.size.width;
    const h = item.size.height;

    // Rotate around center, then translate to fit in bounding box
    if (rotation === 90) {
      // Rotate 90°, then shift so it fits in the swapped bounding box
      return `translate(${h}, 0) rotate(90)`;
    } else if (rotation === 180) {
      return `translate(${w}, ${h}) rotate(180)`;
    } else if (rotation === 270) {
      return `translate(0, ${w}) rotate(270)`;
    }
    return '';
  };

  const getItemDisplaySize = (item) => {
    // When rotated 90 or 270 degrees, swap width and height for display
    const rotation = item.rotation || 0;
    const isVertical = rotation === 90 || rotation === 270;
    if (isVertical) {
      return { width: item.size.height, height: item.size.width };
    }
    return { width: item.size.width, height: item.size.height };
  };

  const startRotate = (item, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Simple 90-degree rotation on click
    const currentRotation = item.rotation || 0;
    item.rotation = (currentRotation + 90) % 360;
    markAsChanged();

    $q.notify({
      type: 'info',
      message: `Rotated to ${item.rotation}°`,
      timeout: 1000,
    });
  };

  // Properties panel handlers
  const handlePropertyChanged = ({ itemId, property, value }) => {
    const item = layoutItems.value.find((i) => i.id === itemId);
    if (!item) {return;}

    if (property === 'position') {
      // Clamp position to board bounds using display size (rotated dimensions)
      const displaySize = getItemDisplaySize(item);
      item.position = {
        x: Math.max(0, Math.min(value.x, boardWidth.value - displaySize.width)),
        y: Math.max(0, Math.min(value.y, boardHeight.value - displaySize.height)),
      };
    } else if (property === 'size') {
      item.size = value;
    } else {
      item[property] = value;
    }
    markAsChanged();
  };

  const handleDeleteItem = (item) => {
    $q.dialog({
      title: 'Remove Item',
      message: `Remove ${item.name} from the board?`,
      cancel: true,
      persistent: true,
    }).onOk(() => {
      // If it's a controller, add it back to the available list
      if (item.type === 'controller' && item.controller_id) {
        availableControllers.value.push({
          id: item.controller_id,
          name: item.name,
          equipment_served: item.equipment_served_list || [],
          dimensions: item.size,
          io_summary: item.io_summary,
        });
      }

      layoutItems.value = layoutItems.value.filter((i) => i.id !== item.id);
      if (selectedItem.value?.id === item.id) {
        selectedItem.value = null;
      }
      markAsChanged();

      $q.notify({
        type: 'positive',
        message: 'Item removed from board',
      });
    });
  };

  const handleSaveLayout = async () => {
    try {
      const layoutData = {
        board: {
          width_mm: boardWidth.value,
          height_mm: boardHeight.value,
          name: boardName.value,
        },
        items: layoutItems.value,
      };


      await saveLayout(layoutData);
      hasChanges.value = false;

      $q.notify({
        type: 'positive',
        message: 'Board layout saved successfully',
      });
    } catch (error) {
      console.error('[Board Canvas] Save error:', error);
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to save board layout',
      });
    }
  };

  // Initialize
  onMounted(async () => {
    try {
      // Load all data in parallel
      await Promise.all([
        loadLayout(),
        loadAccessories(),
        loadAvailableControllers(),
      ]);

      // Apply loaded layout if it exists
      if (layout.value) {
        if (layout.value.board) {
          boardWidth.value = layout.value.board.width_mm || 800;
          boardHeight.value = layout.value.board.height_mm || 600;
          boardName.value = layout.value.board.name || 'DDC Board';
        }
        if (layout.value.items && Array.isArray(layout.value.items)) {
          layoutItems.value = layout.value.items;

          // Filter out controllers that are already placed on the board
          const placedControllerIds = layout.value.items
            .filter((item) => item.type === 'controller' && item.controller_id)
            .map((item) => item.controller_id);

          if (placedControllerIds.length > 0) {
            availableControllers.value = availableControllers.value.filter(
              (c) => !placedControllerIds.includes(c.id),
            );
          }
        }
      }

      hasChanges.value = false;
    } catch (error) {
      console.error('[Board Canvas] Initialization error:', error);
      $q.notify({
        type: 'negative',
        message: 'Failed to load board layout',
      });
    }
  });
</script>

<style scoped>
.ddc-board-canvas {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.canvas-toolbar {
  border-bottom: 1px solid #ddd;
}

.component-library {
  border-right: 1px solid #ddd;
}

.board-canvas {
  background: white;
  border: 1px solid #ddd;
  cursor: default;
  display: block;
}

.board-item {
  cursor: move;
  transition: filter 0.2s;
}

.board-item:hover rect:first-child {
  filter: brightness(0.95);
}

.board-item.selected rect:first-child {
  filter: brightness(0.9);
}

.selection-handles rect {
  pointer-events: none;
}

.rotation-handle {
  cursor: pointer;
  transition: fill 0.2s;
}

.rotation-handle:hover {
  fill: #1976d2;
}

.cursor-grab {
  cursor: grab;
}

.cursor-grab:active {
  cursor: grabbing;
}

.canvas-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.canvas-wrapper svg {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.properties-sidebar {
  border-left: 1px solid #ddd;
}
</style>
